import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { isValidPhoneNumber } from 'libphonenumber-js';

/**
 * User registration input data
 */
export interface UserRegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
}

/**
 * User registration response
 */
export interface UserRegistrationResult {
  success: boolean;
  userID?: string;
  emailDomain?: string;
  message?: string;
  error?: string;
  userObject?: any;
}

/**
 * Validation errors for user registration
 */
export interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  phoneNumber?: string;
}

/**
 * User registration service class
 */
export class UserRegistrationService {
  private readonly MIN_PASSWORD_LENGTH = 8;
  private readonly FREE_EMAIL_DOMAINS = [
    'gmail.com',
    'yahoo.com', 
    'hotmail.com',
    'outlook.com',
    'protonmail.com',
    'icloud.com'
  ];

  /**
   * Register a new user with email and password
   */
  async registerWithEmail(userData: UserRegistrationData): Promise<UserRegistrationResult> {
    try {
      // Validate input data
      const validationErrors = this.validateUserData(userData);
      console.log('Validation errors:', validationErrors);
      console.log('Phone number being validated:', userData.phoneNumber);
      if (Object.keys(validationErrors).length > 0) {
        return {
          success: false,
          error: this.formatValidationErrors(validationErrors)
        };
      }

      // Check for disposable email
      const isDisposable = await this.checkForDisposableEmail(userData.email);
      if (isDisposable) {
        return {
          success: false,
          error: 'Email is disposable or invalid.'
        };
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );

      const user = userCredential.user;

      // Create user in internal API
      await this.createUserInInternalAPI({
        email: user.email!,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber,
        firebaseUID: user.uid
      });

      // Set browser persistence and sign in
      await setPersistence(auth, browserLocalPersistence);
      const signInCredential = await signInWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      );

      // Send email verification
      await sendEmailVerification(auth.currentUser!);

      // Determine company domain
      const emailDomain = userData.email.split('@')[1];
      const companyDomain = this.isCompanyEmail(emailDomain) ? emailDomain : '';

      return {
        success: true,
        userID: signInCredential.user.uid,
        emailDomain: companyDomain,
        message: 'Account created successfully and user doc written.',
        userObject: signInCredential
      };

    } catch (error) {
      console.error('User registration failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  /**
   * Register user with Google OAuth
   */
  async registerWithGoogle(): Promise<UserRegistrationResult> {
    try {
      const googleProvider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, googleProvider);
      
      const user = userCredential.user;
      const userEmail = user.email!;
      const emailDomain = userEmail.split('@')[1];
      const firstName = user.displayName?.split(' ')[0] || '';
      const lastName = user.displayName?.split(' ')[1] || '';
      const phoneNumber = user.phoneNumber || '';

      // Determine company domain
      const companyDomain = this.isCompanyEmail(emailDomain) ? emailDomain : '';

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        biometricsEnabled: 'false',
        currentReadTime: '0',
        email: userEmail,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber,
        notificationCount: '0',
        roles: {
          owner: true,
        },
        createdAt: new Date().toISOString(),
        emailVerified: user.emailVerified
      });

      return {
        success: true,
        userID: user.uid,
        emailDomain: companyDomain,
        message: 'Google account created successfully.',
        userObject: userCredential
      };

    } catch (error) {
      console.error('Google registration failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Google registration failed'
      };
    }
  }

  /**
   * Validate user registration data
   */
  private validateUserData(userData: UserRegistrationData): ValidationErrors {
    const errors: ValidationErrors = {};

    if (!userData.firstName.trim()) {
      errors.firstName = 'First name is required.';
    }

    // DED-1581: Last name is no longer required - users can use pseudo-names
    // if (!userData.lastName.trim()) {
    //   errors.lastName = 'Last name is required.';
    // }

    if (!userData.email.trim()) {
      errors.email = 'Email is required.';
    } else if (!this.isValidEmail(userData.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!userData.password) {
      errors.password = 'Password is required.';
    } else if (userData.password.length < this.MIN_PASSWORD_LENGTH) {
      errors.password = `Password must be at least ${this.MIN_PASSWORD_LENGTH} characters.`;
    }

    if (!userData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required.';
    } else {
      console.log('Validating phone number:', userData.phoneNumber);
      const isValid = isValidPhoneNumber(userData.phoneNumber);
      console.log('Phone number valid:', isValid);
      if (!isValid) {
        errors.phoneNumber = 'Phone number is invalid.';
      }
    }

    return errors;
  }

  /**
   * Check if email is disposable
   */
  private async checkForDisposableEmail(email: string): Promise<boolean> {
    try {
      const response = await fetch(`https://api.mailcheck.ai/email/${email}`);
      const data = await response.json();
      return data.disposable === true;
    } catch (error) {
      console.warn('Disposable email check failed:', error);
      return false; // Don't block registration if service is down
    }
  }

  /**
   * Create user in internal API
   */
  private async createUserInInternalAPI(userData: {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    firebaseUID: string;
  }): Promise<void> {
    const idToken = await auth.currentUser?.getIdToken(true);
    if (!idToken) {
      throw new Error('No authentication token available');
    }

    const cloudFunctionURL = import.meta.env.VITE_cloudFunctionURL;
    const response = await fetch(
      `${cloudFunctionURL}/deiterateInternalAPI/onboarding/createUser`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(userData),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create user in internal API: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Check if email domain is a company email (not free email provider)
   */
  private isCompanyEmail(domain: string): boolean {
    return !this.FREE_EMAIL_DOMAINS.includes(domain.toLowerCase());
  }

  /**
   * Basic email validation
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Format validation errors into a single error message
   */
  private formatValidationErrors(errors: ValidationErrors): string {
    return Object.values(errors).join(' ');
  }
}

// Export singleton instance
export const userRegistrationService = new UserRegistrationService();
